import React, { useState, useEffect } from "react"
import { TextField } from "@mui/material"
import { useCombobox } from "downshift"
import clsx from "clsx"
import { usePostSearch } from "@hooks/usePostSearch"
import { MiniLinkPreview } from "../posts/MiniLinkPreview"
import { DateFormat } from "@util/dates"

const MIN_QUERY_LENGTH = 2

// Consistent styling for all MUI inputs
const muiInputStyles = {
  width: "100%",
}

interface RelatedPostFormFieldProps {
  headerText?: string
  className?: string
  onPostSelect: (posts: any[]) => void
  selectedPosts?: any[]
}

export const RelatedPostFormField: React.FC<RelatedPostFormFieldProps> = ({
  headerText = "Related Posts",
  className = "my-4",
  onPostSelect,
  selectedPosts = [],
}) => {
  const [query, setQuery] = useState("")
  const [searchResults, setSearchResults] = useState([])

  const { isFetching, data, error } = usePostSearch(
    query.length >= MIN_QUERY_LENGTH ? query : "",
    1,
    10,
  )

  // Update search results when data changes
  useEffect(() => {
    if (data?.posts) {
      // Filter out already selected posts
      const filtered = data.posts.filter(
        (post) => !selectedPosts.some((selected) => selected.id === post.id),
      )
      setSearchResults(filtered)
    } else {
      setSearchResults([])
    }
  }, [data, selectedPosts])

  const handlePostSelect = (post: any) => {
    if (!selectedPosts.some((selected) => selected.id === post.id)) {
      const newSelectedPosts = [...selectedPosts, post]
      if (onPostSelect) {
        onPostSelect(newSelectedPosts)
      }
    }
    setInputValue("")
    closeMenu()
  }

  const handleRemovePost = (postId: number) => {
    const newSelectedPosts = selectedPosts.filter((post) => post.id !== postId)
    if (onPostSelect) {
      onPostSelect(newSelectedPosts)
    }
  }

  const handleQueryChange = (inputValue: string) => {
    setQuery(inputValue || "")
  }

  // Downshift combobox hook
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    closeMenu,
    setInputValue,
  } = useCombobox({
    items: searchResults,
    inputValue: query,
    onInputValueChange: ({ inputValue, type }) => {
      if (
        type === useCombobox.stateChangeTypes.ItemClick ||
        type === useCombobox.stateChangeTypes.InputKeyDownEnter
      ) {
        // Item was selected, let onSelectedItemChange handle it
        return
      }
      if (inputValue !== undefined) {
        handleQueryChange(inputValue)
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        handlePostSelect(selectedItem)
      }
    },
    itemToString: (item) => (item ? item.title : ""),
  })

  return (
    <div className={className}>
      <h4 className="uppercase font-bold text-gray-700 text-xs mb-2">
        {headerText}
      </h4>

      {/* Search Input with Downshift Autocomplete */}
      <div className="relative mb-4">
        <TextField
          {...getInputProps({
            placeholder: "Search for posts...",
            onFocus: () => {
              if (searchResults.length > 0) {
                openMenu()
              }
            },
          })}
          size="small"
          sx={muiInputStyles}
        />

        {/* Autocomplete Dropdown */}
        <ul
          {...getMenuProps()}
          className={clsx(
            "absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-sm shadow-lg max-h-60 overflow-auto",
            {
              hidden: !isOpen || searchResults?.length === 0,
            },
          )}
        >
          {isOpen && isFetching && (
            <li className="px-3 py-2 text-gray-500 text-sm">Loading...</li>
          )}
          {isOpen && error && (
            <li className="px-3 py-2 text-red-500 text-sm">
              Error loading posts
            </li>
          )}
          {isOpen &&
            !isFetching &&
            !error &&
            searchResults?.map((post, index) => (
              <li
                key={post.id}
                {...getItemProps({ item: post, index })}
                className={clsx(
                  "px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0",
                  highlightedIndex === index && "bg-blue-100 font-medium",
                  "hover:bg-gray-100",
                )}
              >
                <div className="font-medium text-sm text-black-800 leading-tight">
                  {post.title_cleaned || post.title}
                </div>
                {post.band_name && (
                  <div className="text-xs text-gray-600 italic font-normal mt-1">
                    {DateFormat(post.active_date)} - {post.post_type} by {post.band_name} 
                  </div>
                )}
              </li>
            ))}
        </ul>
      </div>

      {/* Selected Posts */}
      {selectedPosts.length > 0 && (
        <div>
          <ul>
            {selectedPosts.map((post) => (
              <div
                key={`related-post-${post.id}`}
                className="mb-3 flex items-start"
              >
                <div className="flex-1">
                  <MiniLinkPreview post={post} />
                </div>
                <button
                  onClick={() => handleRemovePost(post.id)}
                  className="ml-2 text-red-600 hover:text-red-800 text-sm focus:outline-none"
                  aria-label="Remove post"
                >
                  ×
                </button>
              </div>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
